import { z } from 'zod';
import { EVENT_KINDS } from './events.js';
/** Mirrors `SEALED_ENTITY_KINDS` in e2e.ts (kept here to avoid an import cycle). */
const SEALED_KINDS = [
    'project',
    'workstream',
    'task',
    'rule',
    'idea',
    'card',
    'landing',
    'build',
    'machine',
    'hygiene',
    'handoff',
    'feedback',
    'run.meta',
    'artifact.meta',
    'brain.result',
    'release.request',
];
/** Ciphertext contract (protocol 1). */
export const CONTENT_ENVELOPE_VERSION = 1;
const ContentId = z.string().min(1).max(256);
const Counter = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
export const ContentBase64Url = z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/)
    .refine((s) => {
    const rem = s.length % 4;
    if (rem === 1)
        return false;
    const last = alphabet.indexOf(s.at(-1) ?? '');
    return rem === 2 ? (last & 15) === 0 : rem === 3 ? (last & 3) === 0 : true;
}, 'noncanonical base64url');
const ContentKind = z.enum([
    ...new Set([
        ...EVENT_KINDS,
        'capture',
        'command',
        'command.result',
        'artifact.manifest',
        'artifact.chunk',
        'artifact', // screen-sweep: encrypted artifact index entity
        'inventory', // g10-inventory: per-project stack inventory entity
        'runtitle', // live-activity: the user's run rename entity
        'bark', // bark-triage: barked items (BarkRecord)
        'decision', // phase2-decisions: agent asks with threads (DecisionRecord)
        'task',
        'project',
        'rule',
        'workstream',
        'triage',
        'hygiene',
        'doctor',
        'history',
        'changelog',
        'command.result',
        'dispatch',
        'search',
        'progress',
        'feedbackcfg', // g9-feedback: per-project feedback sources entity
        'visit', // g13-digests: per-project last-visited marker entity
        'setting', // g13-digests: synced settings entity (digests, provider billing)
        'goal', // lanes-integrate: per-project goal + milestones entity
        'research', // g8-voice: research library entity
        'ingestcfg', // ingest-sync: G16 ingest config/status/decision entity
        'ingest', // ingest-sync: G16 ingest item entity
        'run.snapshot', // materialized-state: sealed per-run materialized state snapshot
        'call', // viko-call: call record entity
        ...SEALED_KINDS,
    ]),
]);
export const EncryptionContext = z.strictObject({
    version: z.literal(CONTENT_ENVELOPE_VERSION),
    accountId: ContentId,
    epoch: Counter,
    objectId: ContentId,
    runId: ContentId,
    seq: Counter,
    kind: ContentKind,
});
export const CiphertextBox = z.strictObject({
    version: z.literal(1),
    algorithm: z.literal('A256GCM'),
    iv: ContentBase64Url.refine((s) => s.length === 16, '96-bit IV required'),
    ciphertext: ContentBase64Url.refine((s) => s.length >= 363 && s.length <= 22_369_643, 'invalid ciphertext length'),
});
/** Strict rather than stripping: unknown plaintext fields are a privacy violation. */
export const EncryptedContent = z.strictObject({ context: EncryptionContext, box: CiphertextBox });
/** Additive ciphertext transport; this does not negotiate the full protocol-1 cutover. */
export const CONTENT_PUT_MAX_BYTES = 1_600_000;
export const CONTENT_GET_MAX_BYTES = 4096;
export const ContentPutResult = z.strictObject({ status: z.enum(['stored', 'duplicate']) });
export const ContentRpcFrame = z.discriminatedUnion('method', [
    z.strictObject({
        type: z.literal('rpc'),
        id: z
            .string()
            .min(1)
            .max(128)
            .regex(/^[A-Za-z0-9:_-]+$/),
        method: z.literal('content.put'),
        params: EncryptedContent,
    }),
    z.strictObject({
        type: z.literal('rpc'),
        id: z
            .string()
            .min(1)
            .max(128)
            .regex(/^[A-Za-z0-9:_-]+$/),
        method: z.literal('content.get'),
        params: EncryptionContext,
    }),
]);
const EventRunId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_.-]+$/)
    .refine((id) => !['__proto__', 'constructor', 'prototype'].includes(id));
export const ENCRYPTED_EVENTS_CAPABILITY = 'encrypted-events-v1';
export const ENCRYPTED_EVENT_BATCH_BYTES = 1_500_000;
export const EncryptedTranscriptEvent = z.strictObject({
    context: EncryptionContext.extend({ kind: z.enum(EVENT_KINDS) }),
    box: CiphertextBox,
});
export const ContentEventBatchFrame = z.strictObject({
    type: z.literal('content.event.batch'),
    runId: EventRunId,
    events: z.array(EncryptedTranscriptEvent).min(1).max(64),
    replay: z.boolean().optional(),
    more: z.boolean().optional(),
});
export const ContentSubscribeFrame = z.strictObject({
    type: z.literal('content.subscribe'),
    runId: EventRunId,
    fromSeq: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
});
export const ContentAckFrame = z.strictObject({
    type: z.literal('content.ack'),
    runId: EventRunId,
    seq: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
});
//# sourceMappingURL=content.js.map